"use client";

import Image from "next/image";
import { useState, useCallback, useEffect } from "react";
// Using Unicode quotes instead of icons
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	type CarouselApi,
} from "@/components/ui/carousel";

const LOGO_CONFIGS = {
	eab: { scale: 1.2, paddingConfig: 'pt-5 pl-2 lg:pt-6 lg:pl-3' },
	uob: { scale: 1.1, paddingConfig: 'pt-3 pl-2 lg:pt-4 lg:pl-3' },
	coventry: { scale: 1.1, paddingConfig: 'pt-3 pl-1 lg:pt-4 lg:pl-3' },
	JCC_logo: { scale: 0.85, paddingConfig: 'pt-1 pl-2 lg:pt-2 lg:pl-3' },
	cardiff: { scale: 1.1, paddingConfig: 'pt-1 pl-2 lg:pt-6 lg:pl-3' }
};

export interface QuoteData {
	text: string;
	author: string;
	title: string;
	image: string;
	logo?: string;
}

interface QuoteCarouselProps {
	quotes: QuoteData[];
	className?: string;
	variant?: "default" | "blog-quote";
}

// Variant-specific configurations
const VARIANT_CONFIG = {
	"blog-quote": {
		container: "py-8 bg-ss-lightest-blue rounded-3xl",
		maxWidth: "max-w-4xl",
		imageSize: "w-12 h-12 md:w-16 md:h-16",
		logoSize: "w-4 h-4 md:w-6 md:h-6",
		logoClass: "rounded-full",
		authorText: "text-xs md:text-sm",
		titleText: "text-xs",
		gap: "gap-3",
		dotSize: "w-1 h-1 lg:w-2 lg:h-2"
	},
	"default": {
		container: "py-6 sm:py-8 bg-ss-lightest-blue rounded-2xl sm:rounded-3xl",
		maxWidth: "max-w-7xl",
		imageSize: "w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24",
		logoSize: "w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6 lg:w-8 lg:h-8",
		logoClass: "rounded-xl sm:rounded-2xl",
		authorText: "text-sm sm:text-base md:text-lg",
		titleText: "text-xs sm:text-sm md:text-base",
		gap: "gap-3 sm:gap-4",
		dotSize: "w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4"
	}
};

export function QuoteCarousel({ quotes, className = "", variant = "default" }: QuoteCarouselProps) {
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);

	// Return early if no quotes provided
	if (!quotes || quotes.length === 0) {
		return null;
	}

	const scrollToSlide = useCallback((index: number) => {
		api?.scrollTo(index);
	}, [api]);

	useEffect(() => {
		if (!api) {
			return;
		}

		setCurrent(api.selectedScrollSnap());

		api.on("select", () => {
			setCurrent(api.selectedScrollSnap());
		});
	}, [api]);

	const config = VARIANT_CONFIG[variant];

	const CarouselDots = () => (
		quotes.length > 1 && (
			<div className="flex justify-center mt-8 gap-2">
				{quotes.map((_, index) => (
					<button
						key={index}
						onClick={() => scrollToSlide(index)}
						className={`${config.dotSize} rounded-full transition-all duration-500 ease-out transform hover:scale-125 focus:outline-hidden focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 ${current === index
								? 'bg-linear-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25 scale-110'
								: 'bg-gray-300 hover:bg-gray-400 shadow-sm'
							}`}
						aria-label={`Go to slide ${index + 1} of ${quotes.length}. Use arrow keys or swipe to navigate.`}
						aria-current={current === index ? "true" : "false"}
						tabIndex={0}
					/>
				))}
			</div>
		)
	);

	return (
		<section
			className={`${config.container} ${className} w-full`}
			role="region"
			aria-label={`Quote carousel with ${quotes.length} quotes. Current quote ${current + 1} of ${quotes.length}. Use arrow keys or swipe to navigate.`}
		>
			<div className={`${config.maxWidth} mx-auto`}>
				<Carousel 
					setApi={setApi} 
					className="w-full"
					opts={quotes.length === 1 ? { dragFree: false } : undefined}
				>
					<CarouselContent>
						{quotes.map((quote, index) => (
							<CarouselItem key={index}>
								<div className="flex flex-col gap-3 px-6 h-full">
									{/* Quote Content */}
									<div className="text-left">
										<blockquote className={`mb-${variant === "blog-quote" ? "4" : "6"} relative select-none`}>
											<span className="absolute -top-1 left-0 sm:left-2 text-black text-xl sm:text-2xl font-serif leading-none">"</span>
											<span className={`ml-5 sm:ml-7 mr-0 ${variant === "blog-quote" ? "text-base md:text-lg italic font-semibold" : "text-lg sm:text-xl md:text-2xl"} leading-relaxed`}>
												{quote.text}
											</span>
											<span className="inline text-black text-xl sm:text-2xl font-serif ml-1 align-top leading-none">"</span>
										</blockquote>
									</div>

									{/* Author Section */}
									<div className={`flex justify-start mt-auto`}>
										<div className={`flex flex-row ${config.gap} items-center`}>
											{/* Profile Image */}
											<div className={`relative ${config.imageSize} shrink-0`}>
												<div className={`${config.logoClass === "rounded-full" ? "rounded-full" : "rounded-2xl sm:rounded-3xl"} w-full h-full relative ${variant === "default" ? "overflow-hidden" : ""}`}>
													<Image
														src={quote.image}
														alt={`${quote.author}, ${quote.title}`}
														width={variant === "blog-quote" ? 96 : 600}
														height={variant === "blog-quote" ? 96 : 500}
														className={`w-full h-full object-cover rounded-full`}
													/>
													{/* Logo Overlay */}
													{quote.logo && (
														<div className={`absolute bottom-${variant === "blog-quote" ? "0" : "1"} right-${variant === "blog-quote" ? "0" : "1"} ${variant === "default" ? "sm:bottom-2 sm:right-2" : ""} bg-white ${config.logoClass} p-0.5 ${variant === "default" ? "sm:p-1 md:p-1.5" : ""} shadow-lg ${config.logoSize} flex items-center justify-center`}>
															<Image
																src={quote.logo}
																alt="Organisation logo"
																width={variant === "blog-quote" ? 20 : 64}
																height={variant === "blog-quote" ? 20 : 64}
																className="w-full h-full object-contain rounded-full"
																style={variant === "default" ? {
																	transform: `scale(${Object.entries(LOGO_CONFIGS).find(([key]) => quote.logo?.includes(key))?.[1]?.scale || 1
																		})`
																} : undefined}
															/>
														</div>
													)}
												</div>
											</div>
											{/* Author Info */}
											<div className="text-left">
												<p className={`${config.authorText} font-semibold text-gray-900 leading-tight mb-0!`}>
													{quote.author}
												</p>
												<p className={`${config.titleText} text-gray-700 leading-tight mb-0!`}>
													{quote.title}
												</p>
											</div>
										</div>
									</div>
								</div>
							</CarouselItem>
						))}
					</CarouselContent>
				</Carousel>

				{/* Custom Carousel Dots */}
				<CarouselDots />
			</div>
		</section>
	);
}
